import { ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NbAuthModule } from '@nebular/auth';
import { NbSecurityModule, NbRoleProvider } from '@nebular/security';
import { of as observableOf } from 'rxjs';

import { throwIfAlreadyLoaded } from './module-import-guard';
import {
  AnalyticsService,
  LayoutService,
  SeoService,
  StateService,
} from './utils';
import { SessionData } from './data/system/session';

import { SessionService } from './mock/system/session.service';
import { MockDataModule } from './mock/mock-data.module';
import { Auth } from './data/system/auth';
import { AuthService } from './mock/system/auth.service';
import { FundData } from './data/system/fund';
import { FundService } from './mock/system/fund.service';
import { ChartAccount } from './data/system/chartacount';
import { ChartAccountService } from './mock/system/chartaccount.service';
import { AccountData } from './data/system/account';
import { AccountService } from './mock/system/account.service';
import { AllotmentData } from './data/system/allotment';
import { AllotmentService } from './mock/system/allotment.service';
import { ObligationData } from './data/system/obligation';
import { ObligationService } from './mock/system/obligation.service';
import { PayeeData } from './data/system/Payee';
import { PayeeService } from './mock/system/payee.service';
import { FunctionData } from './data/system/function';
import { FunctionService } from './mock/system/function.service';
import { HrData } from './data/system/hr';
import { HrService } from './mock/system/hr.service';
import { MfoPapData } from './data/system/MfoPap';
import { MfoPapService } from './mock/system/mfopap.service';
import { RealignmentData } from './data/system/realignment';
import { RealignmentService } from './mock/system/realignment.service';
import { ReportsData } from './data/system/reports';
import { ReportsService } from './mock/system/reports.service';
import { RegisterData } from './data/system/register';
import { RegisterService } from './mock/system/register.service';
import { NumberService } from './utils/system/number.service';
import { DataFormatService } from './utils/system/dataFormat.service';
import { SysUtilService } from './utils/system/sysUtil.service';
import { ORSReportUtilService } from './utils/system/orsReportUtil.service';
import { ObligationStatusUtilService } from './utils/system/obligationStatusUtil.service';
import { DashboardData } from './data/system/dashboard';
import { DashboardService } from './mock/system/dashboard.service';
import { SABAUDBReportUtilService } from './utils/system/sabaudbReportUtil.service';
import { SABAUDBReportUtilSBService } from './utils/system/sabaudbReportUtilSB.service';
import { SABAUDBReportUtilSBSUMService } from './utils/system/sabaudbReportUtilSBSUM.service';
import { SABAUDBReportUtilSTFService } from './utils/system/sabaudbReportUtilSTF.service';
import { SABAUDBReportUtilSTFSUMService } from './utils/system/sabaudbReportUtilSTFSUM.service';
import { AuthGuard } from './utils/system/auth.guard';
import { PageGuard } from './utils/system/page.guard';
import { ManageBudgetAccount } from './data/system/manageBudgetAccount';
import { ManageBudgetAccountService } from './mock/system/manage-budget-account.service';
import { FarReportUtilService } from './utils/system/farReportUtil.service';
import { UserDetailsService } from './utils/system/user-details.service';
import { ExcelConsolidatedSABAUDB } from './utils/system/excelConsolidatedSABAUDB.service';
import { RegistryReportService } from './utils/system/registryReport.service';
import { ConsolidatedSABAUDBUtilService } from './utils/system/consolidated-sabaudbutil.service';
import { BudgetReportsSABAUDBUtilService } from './utils/system/budget-reports-sabaudbutil.service';
import { System } from './data/system/system';
import { SystemService } from './mock/system/system.service';
import { UsersService } from './mock/system/users.service';
import { UserData } from './data/system/users';
import { UserTypesService } from './mock/system/user-types.service';
import { UserType } from './data/system/usertype';
import { ExcelCaaListsService } from './utils/system/excel-caa-lists.service';

// const socialLinks = [
//   {
//     url: 'https://github.com/akveo/nebular',
//     target: '_blank',
//     icon: 'github',
//   },
//   {
//     url: 'https://www.facebook.com/akveo/',
//     target: '_blank',
//     icon: 'facebook',
//   },
//   {
//     url: 'https://twitter.com/akveo_inc',
//     target: '_blank',
//     icon: 'twitter',
//   },
// ];

const DATA_SERVICES = [
  { provide: SessionData, useClass: SessionService },
  { provide: Auth, useClass: AuthService },
  { provide: FundData, useClass: FundService },
  { provide: ChartAccount, useClass: ChartAccountService },
  { provide: AccountData, useClass: AccountService },
  { provide: AllotmentData, useClass: AllotmentService },
  { provide: ObligationData, useClass: ObligationService },
  { provide: PayeeData, useClass: PayeeService },
  { provide: FunctionData, useClass: FunctionService },
  { provide: HrData, useClass: HrService },
  { provide: MfoPapData, useClass: MfoPapService },
  { provide: RealignmentData, useClass: RealignmentService },
  { provide: ReportsData, useClass: ReportsService },
  { provide: RegisterData, useClass: RegisterService },
  { provide: DashboardData, useClass: DashboardService },
  { provide: ManageBudgetAccount, useClass: ManageBudgetAccountService },
  { provide: System, useClass: SystemService },
  { provide: UserData, useClass: UsersService },
  { provide: UserType, useClass: UserTypesService }
];

export class NbSimpleRoleProvider extends NbRoleProvider {
  getRole() {
    // here you could provide any role based on any auth flow
    return observableOf('guest');
  }
}

export const NB_CORE_PROVIDERS = [
  ...MockDataModule.forRoot().providers,
  ...DATA_SERVICES,
  ...NbAuthModule.forRoot({
    // strategies: [
    //   NbDummyAuthStrategy.setup({
    //     name: 'email',
    //     delay: 3000,
    //   }),
    // ],
    // forms: {
    //   login: {
    //     socialLinks: socialLinks,
    //   },
    //   register: {
    //     socialLinks: socialLinks,
    //   },
    // },
  }).providers,

  NbSecurityModule.forRoot({
    accessControl: {
      guest: {
        view: '*',
      },
      user: {
        parent: 'guest',
        create: '*',
        edit: '*',
        remove: '*',
      },
    },
  }).providers,

  {
    provide: NbRoleProvider, useClass: NbSimpleRoleProvider,
  },
  AnalyticsService,
  LayoutService,
  SeoService,
  StateService,
  NumberService,
  DataFormatService,
  SysUtilService,
  ORSReportUtilService,
  ObligationStatusUtilService,
  SABAUDBReportUtilService,
  SABAUDBReportUtilSBService,
  SABAUDBReportUtilSBSUMService,
  SABAUDBReportUtilSTFService,
  SABAUDBReportUtilSTFSUMService,
  FarReportUtilService,
  ExcelConsolidatedSABAUDB,
  AuthGuard,
  PageGuard,
  UserDetailsService,
  RegistryReportService,
  ConsolidatedSABAUDBUtilService,
  BudgetReportsSABAUDBUtilService,
  ExcelCaaListsService
];

@NgModule({
  imports: [
    CommonModule,
  ],
  exports: [
    NbAuthModule,
  ],
  declarations: [],
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    throwIfAlreadyLoaded(parentModule, 'CoreModule');
  }

  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        ...NB_CORE_PROVIDERS,
      ],
    };
  }
}
